import React, { useState, useMemo } from 'react';
import { useRecordRefs } from '../utils/recordRefs';
import Icon from './Icon';
import SearchableSelect from './ui/SearchableSelect';
import EmptyState from './EmptyState';
import SortableHeader from './SortableHeader';
import { useSortableTable } from '../hooks/useSortableTable';
import { usePageFilter } from '../contexts/PageFilterContext';
import { usePublishRecordList } from '../hooks/useRecordList';
import type { EntityKind } from '../utils/entityRoutes';
import '../styles/components/DataTable.css';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  /** Long text (notes, descriptions): show at most this many lines, with an ellipsis; the full text is in the tooltip. */
  clamp?: 2 | 3 | 4 | 5;
  // For a column whose displayed value isn't a single raw field (e.g. one
  // that reads a different field depending on row state, the way a "Size"
  // column might show weight_kg for air shipments but total_cbm for sea) —
  // without this, sorting silently falls back to the raw `key` field, which
  // can be null/irrelevant for most rows and make the column look unsortable.
  sortValue?: (row: T) => unknown;
}

type RowActionVariant = 'default' | 'danger' | 'success';

export interface RowAction<T> {
  key: string;
  icon: string;
  title: string | ((row: T) => string);
  onClick: (row: T) => void;
  variant?: RowActionVariant | ((row: T) => RowActionVariant);
  // Lets an action swap appearance based on row state (e.g. Reject ↔
  // Restore) instead of needing two separately-registered actions.
  show?: (row: T) => boolean;
  // Greys the action out while it is in flight, so a second tap cannot repeat it.
  disabled?: (row: T) => boolean;
}

// Builds a synthetic 'actions' column — DataTable already special-cases
// key === 'actions' to skip reading a raw field, so this slots into a
// normal columns array instead of needing a dedicated actions prop.
export function actionsColumn<T>(actions: RowAction<T>[]): Column<T> {
  return {
    key: 'actions',
    header: '',
    render: (_value, row: T) => (
      <div className="dt-row-actions">
        {actions.filter(a => a.show?.(row) ?? true).map(action => {
          const variant = typeof action.variant === 'function' ? action.variant(row) : action.variant;
          return (
            <button
              key={action.key}
              type="button"
              className={`btn-icon${variant ? ` dt-row-action--${variant}` : ''}`}
              title={typeof action.title === 'function' ? action.title(row) : action.title}
              aria-label={typeof action.title === 'function' ? action.title(row) : action.title}
              disabled={action.disabled?.(row) ?? false}
              onClick={e => { e.stopPropagation(); action.onClick(row); }}
            >
              <Icon name={action.icon} size={14} />
            </button>
          );
        })}
      </div>
    ),
  };
}

/** Plain-text cell: an empty value reads as an em-dash, never a blank cell. */
export function cellText(value: unknown, key: unknown): string {
  if (key === 'actions') return '';
  if (value === null || value === undefined) return '\u2014';
  const text = String(value);
  return text.trim() === '' ? '\u2014' : text;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  pagination?: {
    enabled: boolean;
    pageSize?: number;
    showPageSizeSelector?: boolean;
    pageSizeOptions?: number[];
  };
  selectable?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  /**
   * Selection by record, not by position: with all three given, the checkbox column ticks whatever `rowKey` names, so
   * sorting, filtering or paging can never change which record a tick belongs to. The header checkbox selects (or
   * clears) every row the table is showing across its pages, and is indeterminate when only some are ticked.
   */
  rowKey?: (row: T) => string;
  selectedKeys?: ReadonlySet<string>;
  onSelectedKeysChange?: (keys: Set<string>) => void;
  defaultSort?: {
    key: keyof T | string;
    direction: 'asc' | 'desc';
  };
  searchable?: boolean;
  searchPlaceholder?: string;
  /**
   * Row fields the header search must not read, for values the viewer cannot
   * see (money for a financially blind role): matching on a hidden number
   * would reveal it through which rows survive.
   */
  filterOmit?: readonly string[];
  onRowClick?: (row: T, index: number) => void;
  /**
   * Publishes the rows as the table shows them (searched and sorted, across all pages) so the record page a row opens
   * can step to the previous / next one. `getId` defaults to `row.id`.
   */
  recordList?: { kind: EntityKind; getId?: (row: T) => string | number | null | undefined };
  rowClassName?: (row: T, index: number) => string;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  striped = false,
  hoverable = true,
  compact = false,
  pagination,
  selectable = false,
  onSelectionChange,
  rowKey,
  selectedKeys,
  onSelectedKeysChange,
  defaultSort,
  searchable = false,
  searchPlaceholder = 'Search...',
  filterOmit,
  onRowClick,
  recordList,
  rowClassName,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination?.pageSize || 10);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Record numbers arriving change what the ID column sorts on, so the sort
  // accessors are rebuilt (and the rows re-sorted) when they do.
  const { version: recordRefsVersion } = useRecordRefs();
  const sortAccessors = useMemo(() => {
    const accessors: Record<string, (row: T) => unknown> = {};
    for (const column of columns) {
      if (column.sortValue) accessors[String(column.key)] = column.sortValue;
    }
    return accessors;
  }, [columns, recordRefsVersion]);

  const { sortedData: sortedFilteredData, sortConfig, requestSort } = useSortableTable(
    data,
    defaultSort ? { field: String(defaultSort.key), direction: defaultSort.direction } : undefined,
    sortAccessors
  );

  // Enter in the header search narrows whatever page is on screen. Applied
  // here because this is the one component 24 list pages already share —
  // the alternative was a filter prop threaded through every one of them.
  const pageFilter = usePageFilter().trim().toLowerCase();

  const processedData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term && !pageFilter) return sortedFilteredData;
    return sortedFilteredData.filter((row: T) => {
      if (term && !columns.some(column => {
        const value = column.key === 'actions' ? '' : row[column.key as keyof T];
        return String(value).toLowerCase().includes(term);
      })) return false;
      // The page filter scans the whole row, not just the column keys: plenty
      // of tables here render a cell from a field that is not a column key at
      // all (or from a computed one), and a key-only match would report "no
      // results" for a record plainly visible on screen. Nested objects and
      // arrays are skipped — they stringify to "[object Object]" noise.
      if (pageFilter && !Object.entries(row as Record<string, unknown>).some(
        ([k, v]) => !filterOmit?.includes(k) && v != null && typeof v !== 'object' && String(v).toLowerCase().includes(pageFilter)
      )) return false;
      return true;
    });
  }, [sortedFilteredData, searchTerm, pageFilter, columns, filterOmit]);

  // Rows in the order shown, for the record pages' previous / next arrows.
  usePublishRecordList(
    recordList?.kind,
    recordList
      ? processedData.map((row: T) => (recordList.getId ? recordList.getId(row) : (row as Record<string, unknown>).id as string | number | undefined))
      : [],
  );

  const totalPages = pagination?.enabled ? Math.ceil(processedData.length / pageSize) : 1;
  const paginatedData = pagination?.enabled
    ? processedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : processedData;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIndices = paginatedData.map((_: T, index: number) => (currentPage - 1) * pageSize + index);
      setSelectedRows(new Set(allIndices));
      onSelectionChange?.(paginatedData);
    } else {
      setSelectedRows(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (index: number, checked: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    const globalIndex = (currentPage - 1) * pageSize + index;
    if (checked) {
      newSelectedRows.add(globalIndex);
    } else {
      newSelectedRows.delete(globalIndex);
    }
    setSelectedRows(newSelectedRows);
    onSelectionChange?.(Array.from(newSelectedRows).map(idx => data[idx]));
  };

  const isAllSelected = paginatedData.length > 0 && paginatedData.every((_: T, index: number) =>
    selectedRows.has((currentPage - 1) * pageSize + index)
  );

  const isSomeSelected = paginatedData.some((_: T, index: number) =>
    selectedRows.has((currentPage - 1) * pageSize + index)
  );

  const keyed = Boolean(rowKey && selectedKeys && onSelectedKeysChange);
  const showCheckColumn = selectable || keyed;
  const keyedAll = keyed && processedData.length > 0 && processedData.every((r: T) => selectedKeys!.has(rowKey!(r)));
  const keyedSome = keyed && processedData.some((r: T) => selectedKeys!.has(rowKey!(r)));
  const keyedSelectAll = (checked: boolean) => {
    const next = new Set(selectedKeys);
    for (const r of processedData) { if (checked) next.add(rowKey!(r)); else next.delete(rowKey!(r)); }
    onSelectedKeysChange!(next);
  };
  const keyedSelectRow = (row: T, checked: boolean) => {
    const next = new Set(selectedKeys);
    if (checked) next.add(rowKey!(row)); else next.delete(rowKey!(row));
    onSelectedKeysChange!(next);
  };

  const tableClasses = [
    'dt',
    hoverable ? 'dt--hoverable' : '',
    striped ? 'dt--striped' : '',
    compact ? 'dt--compact' : '',
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className="dt-loading">
        <div className="dt-spinner" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className={['dt-wrap', className].filter(Boolean).join(' ')}>
      {searchable && (
        <div className="dt-search">
          <Icon name="search" size={15} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="dt-scroll ds-scroll-x ds-scroll-x--framed">
        <table className={tableClasses}>
          <thead>
            <tr>
              {showCheckColumn && (
                <th className="dt-th-check">
                  <span className="dt-check-wrap">
                    <input
                      type="checkbox"
                      aria-label={keyed ? `Select all ${processedData.length} shown` : 'Select all rows on this page'}
                      title={keyed ? `Select all ${processedData.length} shown` : undefined}
                      checked={keyed ? keyedAll : isAllSelected}
                      ref={(el) => { if (el) el.indeterminate = keyed ? keyedSome && !keyedAll : isSomeSelected && !isAllSelected; }}
                      onChange={(e) => (keyed ? keyedSelectAll(e.target.checked) : handleSelectAll(e.target.checked))}
                    />
                  </span>
                </th>
              )}
              {columns.map(column => (
                <SortableHeader
                  key={String(column.key)}
                  label={column.header}
                  sortKey={String(column.key)}
                  sortConfig={sortConfig}
                  onSort={requestSort}
                  /* dynamic - exempt from guardrail: width/align are column config values */
                  // eslint-disable-next-line react/forbid-component-props
                  style={{ /* ds-dynamic: column width/align from config */ width: column.width, minWidth: column.width, textAlign: column.align }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (showCheckColumn ? 1 : 0)}
                  className="dt-empty"
                >
                  <EmptyState compact title={emptyMessage} />
                </td>
              </tr>
            ) : (
              paginatedData.map((row: T, index: number) => {
                const globalIndex = (currentPage - 1) * pageSize + index;
                const isSelected = keyed ? selectedKeys!.has(rowKey!(row)) : selectedRows.has(globalIndex);
                const extraClass = rowClassName ? rowClassName(row, index) : '';

                return (
                  <tr
                    key={row.id ?? globalIndex}
                    className={[
                      isSelected ? 'dt-row-selected' : '',
                      onRowClick ? 'dt-row-clickable' : '',
                      extraClass,
                    ].filter(Boolean).join(' ')}
                    onClick={() => onRowClick?.(row, index)}
                  >
                    {showCheckColumn && (
                      <td className="dt-td-check">
                        <span className="dt-check-wrap">
                          <input
                            type="checkbox"
                            aria-label={keyed ? `Select ${String(row.name ?? row.id ?? 'row')}` : undefined}
                            checked={isSelected}
                            onChange={(e) => (keyed ? keyedSelectRow(row, e.target.checked) : handleSelectRow(index, e.target.checked))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </span>
                      </td>
                    )}
                    {columns.map(column => {
                      const value = column.key === 'actions' ? null : row[column.key as keyof T];
                      const content = column.render
                        ? column.render(value, row, index)
                        : cellText(value, column.key);

                      return (
                        <td
                          key={String(column.key)}
                          /* dynamic - exempt from guardrail: width/align are column config values passed at runtime */
                          // eslint-disable-next-line react/forbid-component-props
                          style={{ /* ds-dynamic: column width/align from config */ width: column.width, minWidth: column.width, maxWidth: column.width, textAlign: column.align }}
                          className={column.className}
                        >
                          {column.clamp ? (
                            <div
                              className={`dt-clamp dt-clamp--${column.clamp}`}
                              title={typeof content === 'string' && content.length > 80 ? content : undefined}
                            >
                              {content}
                            </div>
                          ) : content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination?.enabled && totalPages > 1 && (
        <div className="dt-pagination">
          <span className="dt-pagination-info">
            {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, processedData.length)} of {processedData.length}
          </span>
          <div className="dt-pagination-controls">
            {pagination.showPageSizeSelector && (
              <SearchableSelect
                className="dt-page-size"
                value={String(pageSize)}
                onChange={v => { setPageSize(Number(v)); setCurrentPage(1); }}
                options={(pagination.pageSizeOptions ?? []).map(s => ({ value: String(s), label: `${s} / page` }))}
              />
            )}
            <button className="dt-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              <Icon name="chevronLeft" size={14} />
            </button>
            <span className="dt-page-label">{currentPage} / {totalPages}</span>
            <button className="dt-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              <Icon name="chevronRight" size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
